// src/store/slices/salesAnalyticsSlice.js
import { createSlice } from '@reduxjs/toolkit';
import Expenses from 'src/views/Expenses/Expenses';

const initialState = {
  filterBy: 'Today',
  from: '',
  to: '',
  tableData: [],
  graphData: { labels: [], data: [] },
  staffGraphData: { categories: [], series: [] },
  cashData: [],
  packagedata: null,
  memberdata: null,
  expenses:0,
  advance: 0,
  totalWithoutWallet: 0,
  totals: {
    totalAmountPaid: 0,
    extraPay: 0,
    paidDues: 0,
    taxAmount: 0,
    dueAmount: 0,
    totalWalletAmount: 0
  },
  loading: false,
  loadingCurrentYear: false,
  selectedView: 'GstBillsPage'
};

const salesAnalyticsSlice = createSlice({
  name: 'salesAnalytics',
  initialState,
  reducers: {
    setFilterParams: (state, action) => {
      state.filterBy = action.payload.filterBy;
      state.from = action.payload.from;
      state.to = action.payload.to;
    },
    setSalesData: (state, action) => {
      const { tableData, graphData, cashData, advance,expenses } = action.payload;
      state.tableData = tableData;
      state.graphData = graphData;
      state.cashData = cashData;
      state.advance = advance;
      state.expenses = expenses
    },
    setPackageMembershipData: (state, action) => {
      state.packagedata = action.payload.packagedata;
      state.memberdata = action.payload.memberdata;
    },
    setStaffGraphData: (state, action) => {
      state.staffGraphData = action.payload;
    },
    setSelectedView: (state, action) => {
      state.selectedView = action.payload;
    },
    calculateTotals: (state) => {
      let extraPay = 0;
      let totalAmountPaid = 0;
      let paidDues = 0;
      let taxAmount = 0;
      let totalWalletAmount = 0;
      let totalWithoutWallet = 0;
      let dueAmount = 0;
      let membershipSales = 0;
      let packageSales = 0;

      state.tableData.forEach((data) => {
        const amountPaidArray = data?.amountPaid || [];
        const totalAmount = Number(data?.totalAmount) || 0;
        const subTotal = Number(data?.subTotal) || 0;
        const paidDue = Number(data?.paidDues) || 0;

        const walletPayments = amountPaidArray.filter(payment => payment.paymentType === 'Wallet');
        const nonWalletPayments = amountPaidArray.filter(payment => payment.paymentType !== 'Wallet');

        const amountPaidWithoutWallet = nonWalletPayments.reduce((acc, payment) => acc + Number(payment.amount || 0), 0);
        const walletAmount = walletPayments.reduce((acc, payment) => acc + Number(payment.amount || 0), 0);
        
        totalWalletAmount += walletAmount;
        totalWithoutWallet += amountPaidWithoutWallet;
        dueAmount += paidDue;

        // Correctly calculate GST by summing the precise gstValue from all line items
        const allItems = [
          ...(data?.services || []),
          ...(data?.products || []),
          ...(data?.memberships || []),
          ...(data?.packages || [])
        ];

        const billGstSum = allItems.reduce((sum, item) => sum + (parseFloat(item.gstValue) || 0), 0);

        // Use line-item sum if property exists, otherwise fallback to root totalTaxAmount or difference
        const hasGstValues = allItems.some(item => Object.prototype.hasOwnProperty.call(item, 'gstValue'));
        const billTax = hasGstValues ? billGstSum : (Number(data?.totalTaxAmount) || Math.max(0, totalAmount - subTotal));
        
        taxAmount += billTax;

        totalAmountPaid += (totalAmount - billTax);

        // Categorize totals by billType for the report cards
        if (data.billType === 'Membership') membershipSales += totalAmount;
        else if (data.billType === 'Package') packageSales += totalAmount;
      });

      state.totals = { 
        totalAmountPaid, 
        taxAmount, 
        paidDues, 
        dueAmount, 
        extraPay, 
        totalWalletAmount, 
        membershipSales,
        packageSales
      };
      state.memberdata = { totalAmount: membershipSales };
      state.packagedata = { totalAmount: packageSales };
      state.totalWithoutWallet = totalWithoutWallet;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setLoadingCurrentYear: (state, action) => {
      state.loadingCurrentYear = action.payload;
    },
    resetSalesAnalytics: () => initialState
  }
});

export const { 
  setFilterParams,
  setSalesData,
  setPackageMembershipData,
  setStaffGraphData,
  setSelectedView,
  calculateTotals,
  setLoading,
  setLoadingCurrentYear,
  resetSalesAnalytics
} = salesAnalyticsSlice.actions;

export default salesAnalyticsSlice.reducer;